import { memo, useEffect, useMemo, useRef } from 'react';
import BodyClass from '@plone/volto/helpers/BodyClass/BodyClass';
import LanguageMap from '@plone/volto/helpers/LanguageMap/LanguageMap';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';
import { useCheckSession } from '@serpro/login-cidadao-volto/hooks/useCheckSession';
import { addPrefixPath } from '@serpro/volto-prefixpath/helpers/Url/Url';
import { openManageCookies } from 'govbr.lgpd.plugin/actions';
import find from 'lodash/find';
import { defineMessages, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import withContrastModeManager from '../ContrastMode/withContrastModeManager';

const messages = defineMessages({
  governmentAgencies: {
    id: 'Government Agencies',
    defaultMessage: 'Government Agencies',
  },
  accessToInformation: {
    id: 'Access to Information',
    defaultMessage: 'Access to Information',
  },
  legislation: { id: 'Legislation', defaultMessage: 'Legislation' },
  accessibility: { id: 'Accessibility', defaultMessage: 'Accessibility' },
});

const normalizeLanguage = (lang) => {
  return lang.toLowerCase() === 'pt-br' ? 'pt' : lang.toLowerCase();
};

const extractLanguageFromPath = (pathname, supportedLanguages) => {
  // Remove a barra inicial e divide por barra
  const pathSegments = pathname.replace(/^\//, '').split('/');
  const firstSegment = pathSegments[0];

  // Verifica se o primeiro segmento é um idioma suportado
  return supportedLanguages.find((lang) => lang === firstSegment) || null;
};

const getLanguageProps = (
  site_properties,
  location,
  supportedLanguages,
  languageList,
) => {
  if (!site_properties?.language_selection) return {};

  const currentLang = extractLanguageFromPath(
    location.pathname,
    supportedLanguages,
  );
  return currentLang
    ? {
        idiomaativo: normalizeLanguage(currentLang),
        idiomas: JSON.stringify(languageList),
      }
    : {};
};

const BarraGovbr = ({ intl, toggleContrastMode, contrastModeOn }) => {
  const barraGovRef = useRef(null);
  const site_properties = useSelector((state) => state.site_properties.data);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { fetchData, barraGovState, data } = useCheckSession();
  const sessionFetchedRef = useRef(false);

  // O `fetchData` do useCheckSession troca de identidade a cada dispatch, porque
  // o reducer `dataSession` recria `userSession`. Depender dele sem guard fecha
  // um laço infinito quando a resposta é 200 sem `headerBarToken` (usuário
  // anônimo): cada resposta gera um novo `fetchData`, que redispara este effect.
  // O guard garante uma única verificação de sessão por montagem.
  useEffect(() => {
    if (sessionFetchedRef.current) {
      return;
    }
    sessionFetchedRef.current = true;
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleLogin = () => {
      history.push({
        pathname: '/login_cidadao',
        state: { redirectUrl: window.location.href },
      });
    };
    const handleLogout = () => {
      history.push({
        pathname: '/logout_cidadao',
        state: { redirectUrl: window.location.href },
      });
    };
    const handleManageCookie = () => {
      dispatch(openManageCookies());
    };
    const handleErroToken = () => {
      //TODO: Deslogar sessao
    };

    window.addEventListener('signedIn', handleLogin);
    window.addEventListener('signedOut', handleLogout);
    window.addEventListener('abrir-cookie-config', handleManageCookie);
    window.addEventListener('ACCESSTOKEN_ERROR', handleErroToken);
    window.addEventListener('troca-contraste', toggleContrastMode);

    return () => {
      window.removeEventListener('signedIn', handleLogin);
      window.removeEventListener('signedOut', handleLogout);
      window.removeEventListener('abrir-cookie-config', handleManageCookie);
      window.removeEventListener('ACCESSTOKEN_ERROR', handleErroToken);
      window.removeEventListener('troca-contraste', toggleContrastMode);
    };
  }, [dispatch, history, toggleContrastMode]);

  useEffect(() => {
    if (!barraGovRef?.current) {
      return;
    }

    // Nome e foto: sempre atualizados, inclusive para limpar no logout.
    // Não condicionar ao `if (data)` — o objeto nunca é nulo (ver doc 06),
    // e omitir a limpeza deixaria dados do usuário anterior na tela.
    barraGovRef.current.nomeCompleto = data?.name || '';
    barraGovRef.current.foto = data?.pictureDataUri || null;

    // Token: só entregue à barra quando existe de verdade.
    // Chamar token(undefined) pode colocar a barra em estado de loading
    // indefinido, dependendo da implementação do web component.
    if (data?.headerBarToken) {
      barraGovRef.current.token?.(data.headerBarToken);
    }

    barraGovRef.current.logado = barraGovState;
  }, [barraGovState, data]);

  const headerLinks = [
    {
      title: intl.formatMessage(messages.governmentAgencies),
      href: 'https://www.gov.br/pt-br/orgaos-do-governo',
    },

    {
      title: intl.formatMessage(messages.accessToInformation),
      href: 'https://www.gov.br/acessoainformacao/pt-br',
    },
    {
      title: intl.formatMessage(messages.legislation),
      href: 'http://www4.planalto.gov.br/legislacao',
    },
    {
      title: intl.formatMessage(messages.accessibility),
      href: 'https://www.gov.br/governodigital/pt-br/acessibilidade-digital',
    },
  ];

  const { supportedLanguages } = config?.settings;
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );

  const languageList = useMemo(() => {
    if (!site_properties?.language_selection) return [];
    return supportedLanguages.map((lang) => {
      const translation = find(translations, { language: lang });
      return {
        title: LanguageMap[lang]?.nativeName,
        codigo: normalizeLanguage(lang),
        link: addPrefixPath(
          translation ? flattenToAppURL(translation['@id']) : `/${lang}`,
        ),
      };
    });
  }, [supportedLanguages, translations, site_properties?.language_selection]);

  return (
    <>
      {contrastModeOn && <BodyClass className="contrast-mode" />}
      <barra-govbr
        ref={barraGovRef}
        id="barraGovBr"
        class="width100 shadow-none show-contrast-toggle"
        titulo={site_properties.organ}
        linklogo={site_properties.govbr_url}
        linkTitulo={site_properties.organ_url}
        linksdosistema={JSON.stringify(headerLinks)}
        ambiente={process.env.RAZZLE_APP_BARRA_GOV_BR_AMBIENTE || 'producao'}
        contrastactive={contrastModeOn}
        {...getLanguageProps(
          site_properties,
          location,
          supportedLanguages,
          languageList,
        )}
      />
    </>
  );
};

export default withContrastModeManager(injectIntl(memo(BarraGovbr)));
